import { Injectable, OnDestroy } from '@angular/core';

/**
 * TabKeepAliveService
 * -------------------
 * Keeps the browser tab exempt from discard / energy-saver / sleeping-tab
 * optimisations (Edge "Performance" features and Chromium tab freezing).
 *
 * DESIGN CONSTRAINT - NO AUDIO
 * ----------------------------
 * A silent looping audio element is the strongest exemption signal, but it is
 * deliberately NOT used here:
 *
 *   - Playing audio can force a Bluetooth headset from A2DP to HFP, degrading
 *     microphone quality mid-call.
 *   - A second HTMLAudioElement does not follow Twilio's speakerDevices /
 *     ringtoneDevices selection, so it can play on the wrong device.
 *   - The tab displays a permanent speaker icon, which agents will query.
 *
 * Instead this service uses only zero-cost, non-audio signals:
 *
 *   1. Web Lock        - a held lock marks the page as doing meaningful work.
 *   2. Title mutation  - observed in chrome://discards as an exemption reason
 *                        ("Tab is updating favicon or title in the background").
 *   3. Notification permission - also listed as an exemption reason.
 *
 * PERFORMANCE
 * -----------
 * The Web Lock is a single never-resolving promise (no polling). The title
 * pulse is one property assignment, driven by the existing heartbeat worker
 * tick rather than its own timer - so no additional timer is created and the
 * pulse itself is never subject to main-thread throttling.
 *
 * NOTE
 * ----
 * These are browser heuristics, not guarantees. They reduce the likelihood of
 * suspension but cannot replace a server-side grace period for cases such as
 * machine sleep or sustained network loss.
 */
@Injectable({ providedIn: 'root' })
export class TabKeepAliveService implements OnDestroy {

  private started = false;

  /** The real title, without the invisible pulse character. */
  private baseTitle = 'Onvida';

  /** Alternates the trailing space on each pulse. */
  private toggle = false;

  /** Releases the Web Lock when the service is destroyed. */
  private releaseLock: (() => void) | null = null;

  // =========================================================
  //  LIFECYCLE
  // =========================================================

  /**
   * Begin keeping the tab active.
   * Call once, alongside starting the heartbeat (e.g. after login).
   * Safe to call more than once - subsequent calls are ignored.
   */
  start(): void {

    if (this.started) {
      return;
    }

    this.started = true;
    this.baseTitle = document.title || 'Onvida';

    this.acquireWebLock();
    this.requestNotificationPermission();

    console.log('[KeepAlive] Tab keep-alive started.');
  }

  /**
   * Stop keeping the tab active and restore the original title.
   * Call on logout / teardown.
   */
  stop(): void {

    if (!this.started) {
      return;
    }

    this.started = false;

    if (this.releaseLock) {
      try {
        this.releaseLock();
      } catch { /* noop */ }
      this.releaseLock = null;
    }

    // Remove any trailing pulse character.
    document.title = this.baseTitle;

    console.log('[KeepAlive] Tab keep-alive stopped.');
  }

  /**
   * Perform one keep-alive pulse.
   *
   * IMPORTANT: call this from the heartbeat worker tick, NOT from a
   * setInterval on the main thread - otherwise the pulse is throttled by
   * exactly the behaviour it is meant to defeat.
   */
  pulse(): void {

    if (!this.started) {
      return;
    }

    // Toggle a trailing space: a genuine title mutation that is invisible
    // to the agent and costs a single property write.
    this.toggle = !this.toggle;
    document.title = this.toggle
      ? this.baseTitle + ' '
      : this.baseTitle;
  }

  /**
   * Update the real title.
   *
   * Any other code that sets document.title should route through here,
   * otherwise the pulse and that code will overwrite each other.
   */
  setBaseTitle(title: string): void {

    this.baseTitle = title || 'Onvida';

    // Re-apply immediately, preserving the current pulse state.
    document.title = this.toggle
      ? this.baseTitle + ' '
      : this.baseTitle;
  }

  /** True while the keep-alive is active (diagnostics). */
  isActive(): boolean {
    return this.started;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  // =========================================================
  //  INTERNALS
  // =========================================================

  /**
   * Hold an exclusive Web Lock for the lifetime of the page.
   *
   * The callback returns a promise that never resolves, so the lock is held
   * until the page unloads or stop() is called. Holding a lock signals to the
   * browser that the page has outstanding work.
   */
  private acquireWebLock(): void {

    try {
      const locks = (navigator as any).locks;

      if (!locks || typeof locks.request !== 'function') {
        console.warn('[KeepAlive] Web Locks API unavailable.');
        return;
      }

      locks.request(
        'onvida-session-keepalive',
        { mode: 'exclusive' },
        () => new Promise<void>((resolve) => {
          // Store the resolver so stop() can release the lock cleanly.
          this.releaseLock = resolve;
        })
      ).catch((err: any) => {
        console.warn('[KeepAlive] Web Lock request failed.', err);
      });

    } catch (err) {
      console.warn('[KeepAlive] Web Lock unavailable.', err);
    }
  }

  /**
   * Granted notification permission is itself an exemption signal.
   * Only prompts when the permission has not already been decided, so an
   * agent who previously denied it is never re-prompted.
   */
  private requestNotificationPermission(): void {

    try {
      if (typeof Notification === 'undefined') {
        return;
      }

      if (Notification.permission === 'default') {
        Notification.requestPermission().catch(() => { /* noop */ });
      }

    } catch { /* noop */ }
  }
}
